import React from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Divider, Typography, Box } from '@mui/material';
import { useSingleSelectProfileStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { useGetProfiles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { ExistingProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import SearchSelector, {
  SearchOption
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/components/SearchSelector';
import { useSearchSelectorStore } from './stores/searchSelectorStore';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { StacklineDefaultProfileImage } from 'src/components/SvgIcons/SvgIcons';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import moment from 'moment';

const convertProfilesToOptions = (profiles: ExistingProfile[]): SearchOption[] => {
  if (!profiles) return [];
  return profiles?.map((profile) => ({
    id: profile.profileId,
    label: profile.profileName,
    updatedAt: profile.updatedAt,
    updatedBy: profile.updatedBy
  }));
};

interface SingleSelectProfileModalProps {
  handleProfileAssignment: (
    updates: Array<{
      retailerSku: string;
      contentGenId: string;
      profileId: string;
      profileName: string;
      profileVersion?: number;
    }>
  ) => void;
}

const ModalContent = ({ handleProfileAssignment }: SingleSelectProfileModalProps) => {
  const { data: profiles, isLoadingProfiles } = useGetProfiles();
  const { closeSingleSelectProfileModal, singleSelectProfileData, setSingleSelectProfileData } =
    useSingleSelectProfileStore();
  const { selectedOption, setSelectedOption } = useSearchSelectorStore();
  const users = useAppSelector((state) => state.user.team);

  const handleSave = () => {
    if (selectedOption && singleSelectProfileData) {
      const selectedProfile = profiles?.data.find((profile) => profile.profileId === selectedOption.id);
      if (selectedProfile) {
        handleProfileAssignment([
          {
            retailerSku: singleSelectProfileData.retailerSku,
            contentGenId: singleSelectProfileData.skuContentGenerationId,
            profileId: selectedProfile.profileId,
            profileName: selectedProfile.profileName,
            profileVersion: selectedProfile.version
          }
        ]);
      }
      setSelectedOption(null);
      setSingleSelectProfileData(null);
      closeSingleSelectProfileModal();
    }
  };

  const handleClose = () => {
    setSelectedOption(null);
    setSingleSelectProfileData(null);
    closeSingleSelectProfileModal();
  };

  const profileOptions = convertProfilesToOptions(profiles?.data || []);

  if (isLoadingProfiles) {
    return <p>Loading...</p>;
  }

  if (!profiles?.data || profiles?.data?.length === 0) {
    return <p>No profiles found</p>;
  }

  const handleProfileChange = (newValue: SearchOption | null) => {
    setSelectedOption(newValue);
  };

  return (
    <>
      <Flex alignItems="center" className="genericModal__header">
        <Text
          variant="h4"
          sx={{
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          Assign Content Profile
        </Text>
        <CloseIcon
          style={{ cursor: 'pointer', width: '24px', height: '24px', marginBottom: 'auto' }}
          onClick={handleClose}
        />
      </Flex>
      <Divider
        sx={{
          marginTop: '22px',
          position: 'relative',
          left: '-100px',
          width: '1000px'
        }}
      />
      <GenericModal.Body>
        <Flex flexDirection="column">
          {singleSelectProfileData && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                marginBottom: '16px',
                color: 'text.secondary'
              }}
            >
              Editing profile for: {singleSelectProfileData.productTitle || singleSelectProfileData.retailerSku}
            </Typography>
          )}
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              marginBottom: '8px'
            }}
          >
            Content Profile
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              marginBottom: '16px'
            }}
          >
            Select a content profile to assign to this product.
          </Typography>
          <Box sx={{ marginLeft: '-12px', width: 'calc(100% + 16px)' }}>
            <SearchSelector
              options={profileOptions}
              value={selectedOption}
              onChange={handleProfileChange}
              placeholder="Search content profiles"
            />
          </Box>
          {selectedOption && (
            <Box
              sx={{
                marginTop: '24px',
                padding: '16px',
                backgroundColor: '#F8F9FA',
                borderRadius: '8px',
                border: '1px solid #E9ECEF'
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: 'text.primary'
                }}
              >
                Profile Details
              </Typography>
              {(() => {
                const selectedProfile = profiles?.data.find((profile) => profile.profileId === selectedOption.id);
                const user = users.find((user) => user.id === selectedOption.updatedBy);

                return (
                  <Flex justifyContent="space-between" alignItems="center">
                    <Flex flexDirection="column">
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          marginBottom: '4px'
                        }}
                      >
                        {selectedOption.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          color: 'text.secondary'
                        }}
                      >
                        Last edited {moment(selectedOption.updatedAt).format('MMM D, YYYY')}
                      </Typography>
                    </Flex>
                    <Flex alignItems="center">
                      <Flex
                        flexDirection="column"
                        sx={{
                          marginRight: '12px',
                          textAlign: 'right'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500
                          }}
                        >
                          {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Stackline'}
                        </Typography>
                        <Typography
                          variant="body3"
                          sx={{
                            fontSize: '11px',
                            color: 'text.secondary'
                          }}
                        >
                          {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Support'}
                        </Typography>
                      </Flex>
                      {user?.id && user?.firstName && user?.lastName ? (
                        <AppProfileIcon userId={user.id} initials={`${user.firstName[0]}${user.lastName[0]}`} />
                      ) : (
                        <StacklineDefaultProfileImage style={{ width: '32px', height: '32px' }} />
                      )}
                    </Flex>
                  </Flex>
                );
              })()}
            </Box>
          )}
        </Flex>
      </GenericModal.Body>
      <GenericModal.Footer>
        <Flex justifyContent="space-between">
          <SlButton onClick={handleClose}>Close</SlButton>
          <SlButton variant="contained" onClick={handleSave} disabled={!selectedOption}>
            Assign
          </SlButton>
        </Flex>
      </GenericModal.Footer>
    </>
  );
};

export const SingleSelectProfileModal = ({ handleProfileAssignment }: SingleSelectProfileModalProps) => {
  const { isSingleSelectProfileModalOpen, closeSingleSelectProfileModal } = useSingleSelectProfileStore();

  return (
    <GenericModal
      open={isSingleSelectProfileModalOpen}
      onClose={closeSingleSelectProfileModal}
      sx={{
        '& .MuiPaper-root': {
          width: '752px',
          height: '641px'
        },
        '& .genericModal__body': {
          gap: '16px',
          padding: '24px 28px'
        }
      }}
    >
      <ModalContent handleProfileAssignment={handleProfileAssignment} />
    </GenericModal>
  );
};
